import { Ref, shallowRef, triggerRef } from 'vue'

import { getVolume, getParticipantShortCallIdFromTrackId } from '@/shared/services/media-helper'

import { FIRST_STREAM_ID_SUFFIX } from './use-session-facade'

const FFT_SIZE = 512

let context: AudioContext

const volumeByStreamId: Ref<{
  [streamId: string]: number,
}> = shallowRef({})

const streamIdById: Ref<{
  [number: string]: string,
}> = shallowRef({})

export function useStreamVolume () {
  let intervalId: NodeJS.Timeout | undefined
  let stream: MediaStream | undefined
  let track: MediaStreamTrack | undefined
  let analyser: AnalyserNode

  if (!context) {
    if (typeof AudioContext === 'function') {
      context = new AudioContext()
    } else {
      console.warn('Sorry! Web Audio is not supported by this browser')
    }
  }

  const stopWatch = () => {
    if (intervalId) {
      clearInterval(intervalId)
    }
    if (stream?.id && track) {
      const id = getParticipantShortCallIdFromTrackId(track.id)
      if (id) {
        delete streamIdById.value[id]
      }

      delete volumeByStreamId.value[stream?.id]

      triggerRef(volumeByStreamId)
      triggerRef(streamIdById)
    }
    if (analyser) {
      analyser.disconnect()
    }
  }

  const watch = (mediaStream: MediaStream, subscriberNumber?: string) => {
    stream = mediaStream
    track = mediaStream.getAudioTracks().at(0)
    if (!context || !mediaStream?.id || mediaStream.id.endsWith(FIRST_STREAM_ID_SUFFIX) || !track) {
      return
    }
    const source = context.createMediaStreamSource(mediaStream)

    analyser = context.createAnalyser()
    analyser.fftSize = FFT_SIZE
    analyser.smoothingTimeConstant = 0.45
    const fftBins = new Float32Array(analyser.frequencyBinCount)
    source.connect(analyser)

    intervalId = setInterval(() => {
      analyser.getFloatTimeDomainData(fftBins)
      const volume = getVolume(fftBins)

      if (volume && mediaStream?.id) {
        const id = subscriberNumber ?? getParticipantShortCallIdFromTrackId(track?.id as string)
        if (id) {
          streamIdById.value[id] = mediaStream.id
        }
        volumeByStreamId.value[mediaStream?.id] = volume
        triggerRef(volumeByStreamId)
        triggerRef(streamIdById)
      }
    }, 100)

    return stopWatch
  }

  const getVolumeByStreamId = (streamId: string) => {
    return volumeByStreamId.value[streamId]
  }

  const getVolumeById = (id: string) => {
    const streamId = streamIdById.value[id]
    return streamId ? volumeByStreamId.value[streamId] : undefined
  }

  return {
    watch,
    getVolumeByStreamId,
    getVolumeById,
  }
}
